import * as axios from "axios";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";
import {
	MaintenanceStatus,
	Organization,
	Session,
	SessionNotificationCounts,
	URLs,
	User,
} from "@/type/api";
import { apiClient, AxiosErrorJSON } from "@/client";
import { identifySentrySession } from "@/sentry-report";

// Runtime guard for the /api/session payload. The store must never hand a
// non-session value to callers: a 200 whose body is not a Session object (the
// SPA index.html answered for an unregistered route by an out-of-sync build,
// an empty body, a proxy artifact) should fail the fetch instead of crashing
// consumers on missing fields (e.g. s.self).
function isSessionPayload(data: unknown): data is Session {
	if (typeof data !== "object" || data === null) {
		return false;
	}
	const d = data as Record<string, unknown>;
	return (
		typeof d.self === "object" &&
		d.self !== null &&
		typeof d.organization === "object" &&
		d.organization !== null &&
		typeof d.urls === "object" &&
		d.urls !== null
	);
}

export class ErrorNotSignedIn extends Error {
	constructor() {
		super("not signed in");
		this.name = "ErrorNotSignedIn";
		Object.setPrototypeOf(this, ErrorNotSignedIn.prototype);
	}
}

export interface SigninResult {
	is_success: boolean;
	status: number;
	message: string;
}
export const useSessionStore = defineStore("session", () => {
	// State
	const hasSession = ref<boolean>(false);
	const isAuthenticated = ref<boolean>(false);
	const isLoading = ref(true);
	const impersonating = ref<string | null>(null);
	const error = ref<string | null>(null);
	const current = ref<Session | null>(null);
	const notification_counts = ref<SessionNotificationCounts | null>(null);
	const ongoingFetch = ref<Promise<Session> | null>(null);
	const organization = ref<Organization | null>(null);
	const self = ref<User | null>(null);

	/** The TS mirror of `platform.IsOrgAdmin`, and the only definition of "this
	 * member may change the district's configuration" in the SPA. Root and
	 * account-owner; cosmetic beside the server's own check (§8.1), which is what
	 * actually refuses the write. */
	const isOrgAdmin = computed(
		() => self.value?.role === "root" || self.value?.role === "account-owner",
	);
	const urls = ref<URLs | null>(null);
	// Per-host maintenance gates + optional restore-rebuild progress
	// (GET /api/maintenance). null = never fetched or explicitly cleared; the
	// app shell and sudo panel react to the gates it reports.
	const maintenance = ref<MaintenanceStatus | null>(null);

	// Subscription
	SSEManager.subscribe((msg: SSEMessageResource) => {
		if (msg.resource.startsWith("sync:session")) {
			fetchSession();
		} else if (msg.resource === "maintenance") {
			// A gate armed/cleared or the restore pipeline progressed: refresh
			// the status. Fire-and-forget — fetchMaintenance is failure-tolerant.
			fetchMaintenance().catch(() => {});
		}
	});

	// Actions
	async function doSignin(
		password: string,
		username: string,
	): Promise<SigninResult> {
		try {
			console.log("begin signin request");
			await apiClient.JSONPost("/api/signin", {
				password: password,
				username: username,
			});
			// A fresh sign-in must not inherit session state cached from a
			// previous user (the SPA is not reloaded between logins): clear it
			// so the next get() fetches the new session.
			resetSessionState();
			isAuthenticated.value = true;
			// A fresh sign-in must re-probe the gates: any maintenance state
			// cached from a previous session is stale for this user.
			clearMaintenance();
			console.log("set authenticated to true after signin request");
			return {
				is_success: true,
				status: 200,
				message: "",
			};
		} catch (e: any) {
			if (e instanceof axios.AxiosError && e.response) {
				return {
					is_success: false,
					status: e.response.status,
					message: e.response.data?.message || "",
				};
			}
			return {
				is_success: false,
				status: 0,
				message: e?.message || "Login failed",
			};
		}
	}
	async function fetchSession(): Promise<Session> {
		error.value = null;

		try {
			const data: unknown = await apiClient.JSONGet("/api/session");
			if (!isSessionPayload(data)) {
				error.value =
					"Session response was not a valid session payload. Try reloading the page.";
				throw new Error(
					"GET /api/session returned a payload that is not a session",
				);
			}
			isAuthenticated.value = true;
			console.log(
				"set authenticated",
				isAuthenticated.value,
				"due to successful GET /api/session",
			);
			current.value = data;
			impersonating.value = data.impersonating || null;
			notification_counts.value = data.notification_counts;
			organization.value = data.organization;
			self.value = data.self;
			urls.value = data.urls;
			// Every later event — the ledger's timeout reports included — is
			// attributed to this operator and the district they read under.
			identifySentrySession(data);
			return data;
		} catch (e: any) {
			const data: AxiosErrorJSON =
				e instanceof axios.AxiosError
					? (e.toJSON() as AxiosErrorJSON)
					: { status: 0, message: "" };
			if (data.status == 401) {
				throw new ErrorNotSignedIn();
			}
			console.error("Error fetching session:", e);
			throw e;
		} finally {
			hasSession.value = true;
			isLoading.value = false;
			console.log("no longer loading session");
		}
	}

	async function getAuthenticated(): Promise<boolean> {
		await get();
		return isAuthenticated.value;
	}

	async function get(): Promise<Session> {
		if (current.value != null) {
			return current.value;
		}

		// Memoize the in-flight fetch so concurrent callers (e.g. the
		// authenticated layout and its sidebar mounting together) share one
		// request instead of each issuing their own.
		let inFlight = ongoingFetch.value;
		if (inFlight === null) {
			inFlight = fetchSession().finally(() => {
				ongoingFetch.value = null;
			});
			ongoingFetch.value = inFlight;
		}
		return inFlight;
	}

	// Drop all cached session state. Used on sign-out and before a fresh
	// sign-in so the app can never keep serving a previous user's session from
	// memory when the SPA is not reloaded between logins.
	function resetSessionState(): void {
		hasSession.value = false;
		isAuthenticated.value = false;
		current.value = null;
		impersonating.value = null;
		notification_counts.value = null;
		organization.value = null;
		self.value = null;
		urls.value = null;
		identifySentrySession(null);
	}

	async function signout(): Promise<void> {
		resetSessionState();
		console.log("set authenticated", isAuthenticated.value, "due to signout");
		apiClient.JSONPost("/api/signout", {});
		// The next sign-in belongs to a fresh session: drop any maintenance
		// state cached from this one so a new user re-probes the gates.
		clearMaintenance();
	}

	// fetchMaintenance loads the per-host maintenance-gate status
	// (GET /api/maintenance) into the store. It is intentionally tolerant of
	// the failures the maintenance flow produces: the endpoint is whitelisted
	// from the gate but still requires a session, so a 401 (no session) or 503
	// (gate answering) tells a probing caller nothing actionable. In those
	// cases any previously-known state is left in place and null is returned
	// WITHOUT throwing, so the app shell and the SSE handler can probe freely.
	// Any other failure (network, 5xx) is rethrown for real callers to handle.
	async function fetchMaintenance(): Promise<MaintenanceStatus | null> {
		try {
			const data: MaintenanceStatus =
				await apiClient.JSONGet("/api/maintenance");
			maintenance.value = data;
			return data;
		} catch (e: unknown) {
			const err: AxiosErrorJSON =
				e instanceof axios.AxiosError
					? (e.toJSON() as AxiosErrorJSON)
					: { status: 0, message: "" };
			if (err.status == 401 || err.status == 503) {
				return null;
			}
			console.error("Error fetching maintenance status:", e);
			throw e;
		}
	}

	// clearMaintenance drops cached gate state (used on sign-in/sign-out so the
	// next session starts from a clean probe).
	function clearMaintenance(): void {
		maintenance.value = null;
	}
	async function endImpersonation(): Promise<Session> {
		await apiClient.JSONDelete(urls.value!.api.impersonation);
		return fetchSession();
	}
	return {
		// State
		error,
		getAuthenticated,
		hasSession,
		impersonating,
		isAuthenticated,
		isLoading,
		isOrgAdmin,
		maintenance,
		notification_counts,
		organization,
		self,
		urls,
		// Actions
		clearMaintenance,
		doSignin,
		endImpersonation,
		fetchMaintenance,
		fetchSession,
		get,
		signout,
	};
});
